import { t } from "../services/utils";
import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import Divider from "@material-ui/core/Divider";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import { withStyles } from "@material-ui/core/styles";
import _ from "lodash";
import groupTypes from "../constants/group-types";
import { primary5, primaryDark40, primaryDark80 } from "../constants/colors";
import IconMoon from "./IconMoon";
import { useGetTrainersQuery } from "../data/trainers/trainersApi";
import { useGetGroupsQuery } from "../data/groups/groupsApi";
import { Trainer } from "src/types/trainer";
import { Segment } from "src/types/segment";

interface ClientsFilterProps {
  classes: {
    root: string;
    icon: string;
    menuItem: string;
    menuItemSelected: string;
  };
  style?: React.CSSProperties;
  selectDisplayProps?: React.HTMLAttributes<HTMLDivElement>;
  disableUnderline?: boolean;
  width?: number;
  filterGroups?: boolean;
  forAll: boolean;
  forTrainer: boolean;
  id: number;
  onChange: (filter: FilterOption) => void;
}

interface FilterOption {
  forAll: boolean;
  forTrainer: boolean;
  id: number;
}

const styles = {
  root: {
    padding: "10px 18px 10px 20px",
    lineHeight: "20px",
  },
  icon: {
    right: "5px !important",
  },
  menuItem: {
    "&:hover": {
      backgroundColor: primary5,
    },
  },
  menuItemSelected: {
    backgroundColor: primary5 + " !important",
  },
};

const getObjectFromKey = (key: string): FilterOption => {
  if (key === "all") {
    return { forAll: true, forTrainer: false, id: 0 };
  }

  const split = key.split("-");
  if (split[0] === "tr") {
    return {
      forAll: false,
      forTrainer: true,
      id: parseInt(split[1], 10),
    };
  }

  return {
    forAll: false,
    forTrainer: false,
    id: parseInt(split[1], 10),
  };
};

const getSelectKey = (
  forAll: boolean,
  forTrainer: boolean,
  id: number,
): string => {
  if (forAll) return "all";
  if (forTrainer) return `tr-${id}`;
  return `gr-${id}`;
};

const ClientsFilter = ({
  classes,
  style,
  selectDisplayProps,
  disableUnderline,
  width,
  filterGroups,
  forAll,
  forTrainer,
  id,
  onChange,
}: ClientsFilterProps) => {
  const { data: trainers = {} } = useGetTrainersQuery();
  const { data: groups = {} } = useGetGroupsQuery();
  const me = useSelector((state: { me: Trainer }) => state.me);

  const handleChange = useCallback(
    (e: React.ChangeEvent<{ value: unknown }>) => {
      const val = e.target.value as string;
      onChange(getObjectFromKey(val));
    },
    [onChange],
  );

  const componentStyles = useMemo(
    (): Record<string, React.CSSProperties> => ({
      empty: {
        height: 62,
        width: width,
      },
      header: {
        color: primaryDark40,
        marginTop: 10,
        display: "flex",
        alignItems: "center",
        opacity: 1,
        fontSize: 14,
        lineHeight: "20px",
        fontWeight: "500",
        padding: "10px 16px 10px 16px",
      },
      filter: {
        display: "flex",
        backgroundColor: "white",
        fontSize: 14,
        border: "1px solid #CCC9D5",
        borderRadius: 6,
        width: 160,
      },
      menuItem: {
        color: primaryDark80,
        borderBottom: "1px solid #EAE9EF",
        padding: "10px 16px 10px 16px",
        fontSize: 14,
        lineHeight: "20px",
        fontWeight: "500",
      },
    }),
    [width],
  );

  const menuItems = useMemo(() => {
    if (_.isEmpty(trainers) || _.isEmpty(groups)) {
      return null;
    }

    const items: React.ReactElement[] = [
      <MenuItem
        key="all"
        value="all"
        style={componentStyles.menuItem}
        className={classes.menuItem}
        classes={{ root: classes.menuItem, selected: classes.menuItemSelected }}
      >
        {t("allClients")}
      </MenuItem>,
    ];

    // Add trainers section
    items.push(
      <MenuItem
        key="1"
        value="1"
        disabled={true}
        style={componentStyles.header}
      >
        <IconMoon icon="User" style={{ color: primaryDark40 }} />
        <span style={{ marginLeft: 12 }}>{t("trainedBy")}</span>
      </MenuItem>,
    );
    items.push(<Divider key="2" />);

    // Add trainer options
    const trainersArray = Object.values(trainers);
    trainersArray.forEach((trainer) => {
      const key = getSelectKey(false, true, trainer.id);
      const name =
        trainer.id === me.id
          ? t("myClients")
          : `${trainer.first_name} ${trainer.last_name}`;

      items.push(
        <MenuItem
          key={key}
          value={key}
          style={componentStyles.menuItem}
          classes={{
            root: classes.menuItem,
            selected: classes.menuItemSelected,
          }}
        >
          {name}
        </MenuItem>,
      );
    });

    items.push(
      <MenuItem
        key="3"
        value="3"
        disabled={true}
        style={componentStyles.header}
      >
        <IconMoon icon="Users" style={{ color: primaryDark40 }} />
        <span style={{ marginLeft: 12 }}>{t("inGroup")}</span>
      </MenuItem>,
    );
    items.push(<Divider key="4" />);

    // Add group options
    let groupsArray = Object.values(groups);
    if (filterGroups) {
      groupsArray = groupsArray.filter((group: Segment) => !group.for_tm_only);
    }
    groupsArray = groupsArray.filter(
      (group: Segment) => group.segment_type === groupTypes.FOR_GROUP,
    );

    for (let i = 0; i < groupsArray.length; i++) {
      let group = groupsArray[i];
      let key = getSelectKey(false, false, group.id);
      items.push(
        <MenuItem
          key={key}
          value={key}
          style={componentStyles.menuItem}
          classes={{
            root: classes.menuItem,
            selected: classes.menuItemSelected,
          }}
        >
          {group.name}
        </MenuItem>,
      );
    }

    return items;
  }, [
    trainers,
    groups,
    me.id,
    filterGroups,
    componentStyles.menuItem,
    componentStyles.header,
    classes.menuItem,
    classes.menuItemSelected,
  ]);

  const currentValue = useMemo(
    () => getSelectKey(forAll, forTrainer, id),
    [forAll, forTrainer, id],
  );

  if (_.isEmpty(trainers) || _.isEmpty(groups)) {
    return <div style={componentStyles.empty} />;
  }

  return (
    <Select
      value={currentValue}
      data-testid="clients-filter"
      style={{ ...componentStyles.filter, ...style }}
      onChange={handleChange}
      MenuProps={{
        getContentAnchorEl: null,
        anchorOrigin: { vertical: "top", horizontal: "left" },
        MenuListProps: { style: { padding: 0 } },
      }}
      classes={{ root: classes.root, icon: classes.icon }}
      SelectDisplayProps={selectDisplayProps}
      disableUnderline={disableUnderline}
    >
      {menuItems}
    </Select>
  );
};

export default withStyles(styles)(ClientsFilter);
